import { useMemo, type CSSProperties } from "react";
import { Stack } from "@volca/ui/components/ui/stack";
import { cn } from "@volca/ui/lib/utils";
import { useResolvedTheme } from "@volca/ui/theme";
import { readableTextColor } from "@/payroll/ai-script/ai-script-spreadsheet-viewer/adapter/cell-format";
import { flattenColor } from "@/payroll/ai-script/ai-script-spreadsheet-viewer/color-contrast";
import { volcaColor } from "@/payroll/ai-script/ai-script-spreadsheet-viewer/spreadsheet-theme";
import { CELL_MARK_COLOR } from "@/payroll/editor/diff-marks";
import { COLUMN_LETTERS, coordKey } from "./cell-marks";
import type {
  CellMark,
  ComparisonCellData,
  ComparisonCellStyle,
  ComparisonGrid,
} from "./types";

export { cellAddress, MARK_LABEL, markSet, markCount } from "./cell-marks";
export type { CellMark } from "./types";

export const MARK_COLOR = CELL_MARK_COLOR;

export const markRing = (mark: CellMark): CSSProperties => ({
  boxShadow: `inset 0 0 0 ${mark === "format" ? 1 : 2}px ${MARK_COLOR[mark]}`,
  outline: mark === "format" ? `1px dashed ${MARK_COLOR[mark]}` : undefined,
  outlineOffset: mark === "format" ? "-3px" : undefined,
});

const SELECTED_STYLE: CSSProperties = {
  boxShadow: "inset 0 0 0 2px var(--selected-line)",
};

const SELECTED_RING = "relative z-6";

const HEADER_ACTIVE = "bg-neutral text-ink";

const DEFAULT_WIDTH = 132;
const ARROWS: Record<string, [number, number]> = {
  ArrowUp: [-1, 0],
  ArrowDown: [1, 0],
  ArrowLeft: [0, -1],
  ArrowRight: [0, 1],
};

const DEFAULT_HEIGHT = 30;

const cellTextColor = (style: ComparisonCellStyle | null | undefined) => {
  if (!style) return undefined;
  const { bg, color } = style;
  if (bg && color) return color;
  if (!bg && !color) return undefined;

  const background = bg
    ? flattenColor(bg, volcaColor("--surface-sunken"))
    : volcaColor("--surface-raised");
  return readableTextColor(background, color);
};

const cellStyle = (
  cell: ComparisonCellData | undefined,
): React.CSSProperties => {
  const own = cell?.style;
  return {
    background: own?.bg,
    color: cellTextColor(own),
    fontWeight: own?.bold ? 600 : undefined,
    textAlign: own?.align,
    verticalAlign: own?.vertical,
    whiteSpace: own?.wrap ? "normal" : "nowrap",
  };
};

export const ComparisonGridView = ({
  grid,
  marks,
  rows,
  cols,
  emptyLabel,
  viewportRef,
  describe,
  selected,
  onSelect,
}: {
  grid: ComparisonGrid | null;
  marks: Map<string, CellMark>;
  rows: number;
  cols: number;
  emptyLabel: string;
  viewportRef?: React.Ref<HTMLDivElement>;
  describe?: (row: number, col: number) => string | undefined;
  selected?: string | null;
  onSelect?: (coord: string) => void;
}) => {
  useResolvedTheme();
  const byCoord = useMemo(() => {
    const map = new Map<string, ComparisonCellData>();
    for (const cell of grid?.cells ?? [])
      map.set(coordKey(cell.row, cell.col), cell);
    return map;
  }, [grid]);

  if (!grid)
    return (
      <Stack
        gap={0}
        align="stretch"
        direction="row"
        className="flex h-full items-center justify-center p-6 text-xs text-subtle"
      >
        {emptyLabel}
      </Stack>
    );

  const [activeRow, activeCol] = (selected ?? "").split(":").map(Number);
  const widths = grid.column_widths ?? [];
  const heights = grid.row_heights ?? [];

  return (
    <div
      ref={viewportRef}
      className="h-full w-full overflow-auto bg-surface-sunken"
    >
      <table
        tabIndex={0}
        aria-label="Comparison cells. Use the arrow keys to move."
        onKeyDown={(event) => {
          const step = ARROWS[event.key];
          if (!step || !onSelect) return;
          event.preventDefault();
          const [row, col] = (selected ?? "").split(":").map(Number);
          const next = coordKey(
            Math.min(Math.max((row || 1) + step[0], 1), rows),
            Math.min(Math.max((col || 1) + step[1], 1), cols),
          );
          onSelect(next);
          event.currentTarget
            .querySelector(`[data-coord="${next}"]`)
            ?.scrollIntoView({ block: "nearest", inline: "nearest" });
        }}
        className="border-separate border-spacing-0 text-sm outline-hidden"
        style={{ tableLayout: "fixed" }}
      >
        <colgroup>
          <col style={{ width: 40 }} />
          {Array.from({ length: cols }, (_, index) => (
            <col
              key={index}
              style={{ width: widths[index] ?? DEFAULT_WIDTH }}
            />
          ))}
        </colgroup>
        <thead>
          <tr>
            <th className="sticky top-0 left-0 z-20 h-6 border-r border-b border-line bg-surface-sunken" />
            {Array.from({ length: cols }, (_, index) => (
              <th
                key={index}
                className={cn(
                  "sticky top-0 z-10 h-6 border-r border-b border-line bg-surface-sunken px-2 text-center font-semibold text-subtle",
                  { [HEADER_ACTIVE]: activeCol === index + 1 },
                )}
              >
                {COLUMN_LETTERS(index + 1)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }, (_, rowIndex) => {
            const row = rowIndex + 1;
            return (
              <tr
                key={row}
                style={{ height: heights[rowIndex] ?? DEFAULT_HEIGHT }}
              >
                <th
                  className={cn(
                    "sticky left-0 z-10 border-r border-b border-line bg-surface-sunken px-1 text-center align-middle font-semibold text-subtle",
                    { [HEADER_ACTIVE]: activeRow === row },
                  )}
                >
                  {row}
                </th>
                {Array.from({ length: cols }, (_, colIndex) => {
                  const col = colIndex + 1;
                  const key = coordKey(row, col);
                  const cell = byCoord.get(key);
                  const mark = marks.get(key);
                  const overflow = cell?.overflow ?? undefined;
                  const background = cell?.style?.bg;
                  const overflowBackground =
                    overflow && background
                      ? flattenColor(background, volcaColor("--surface-sunken"))
                      : undefined;
                  return (
                    <td
                      key={col}
                      data-coord={key}
                      title={describe?.(row, col) ?? cell?.value}
                      style={{
                        ...cellStyle(cell),
                        ...(mark && selected !== key ? markRing(mark) : {}),
                        ...(selected === key ? SELECTED_STYLE : {}),
                      }}
                      onClick={() => onSelect?.(key)}
                      className={cn(
                        "relative cursor-pointer border-r border-b border-line bg-surface-raised px-2 py-1 align-middle",
                        {
                          "overflow-visible": overflow,
                          "overflow-hidden text-ellipsis": !overflow,
                          [SELECTED_RING]: selected === key,
                        },
                      )}
                    >
                      {overflow ? (
                        <span
                          className={cn(
                            "pointer-events-none absolute inset-y-0 left-0 z-2 flex items-center px-2",
                            { "bg-surface-raised": !background },
                          )}
                          style={{
                            width: overflow,
                            background: overflowBackground,
                          }}
                        >
                          {cell?.value}
                        </span>
                      ) : (
                        (cell?.value ?? "")
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
